/**
 * Backend `Align` position-ratio convention (see Libertyfi.api
 * `rendering/sprite.py::_get_x_coordinate`):
 *
 * - a NON-NEGATIVE `horizontal_ratio`/`vertical_ratio` places the box's NEAR
 *   edge (left/top) at `ratio · canvas`;
 * - a NEGATIVE ratio places the box's FAR edge (right/bottom) inset `|ratio|`
 *   from the canvas far edge, i.e. `pixel = (1 + ratio) · canvas − boxSize`.
 *
 * The negative branch is an intentional, CSS-`right`/`bottom`-like convention
 * that production templates use to anchor elements to the far edge — it is not
 * a bug and must not be "fixed" in the renderer.
 *
 * The editor, however, stores a plain LINEAR top-left fraction. A cropped item
 * whose media box spills off the top/left therefore has a negative linear
 * position. Serialized raw, the renderer would read it as a far-edge anchor and
 * shift the box by `(1 − size) · canvas`. So off-canvas (negative) positions
 * must be re-expressed in the renderer's far-edge form on save, and decoded
 * back on load.
 *
 * `size` is the box's width (for x) / height (for y) as a fraction of the
 * canvas — i.e. the item's `Width`/`Height` attribute `rate`.
 *
 * Note: the round-trip is exact except for a box wider than the whole canvas
 * that also spills off the left/top (`linear < 0 && linear + size >= 1`), which
 * the renderer's single-ratio `Align` simply cannot represent.
 */
export const toServerAlignRatio = (linear: number, size: number): number =>
  linear >= 0 ? linear : linear + size - 1;

/** Inverse of {@link toServerAlignRatio}: renderer ratio → editor linear fraction. */
export const fromServerAlignRatio = (ratio: number, size: number): number =>
  ratio >= 0 ? ratio : ratio + 1 - size;
